// Finding the pair of statements whose interleaving lost an update.
//
// This is step 1 of docs/design/race-suggestions.md: the *detector*, a pure
// function over a run. It proposes candidates and nothing more — the design's
// whole point is that proposing is easy and deciding is hard, so the deciding
// (does re-running actually show two outcomes?) lives in a later step. Read this
// as "here are the pairs worth running", not "here are the bugs".
//
// **The shape** is the classic atomicity violation: a thread does two things to
// one address, meaning them as one, and another thread's *unit* lands in
// between. What has changed (change (a) of docs/design/txn-granular-exploration.md)
// is what "thing" and "in between" mean:
//
//   - the actors are **units**, not accesses — a committed `txn` is one
//     indivisible unit, a bare access is a unit of its own (`conflict.ts`). So a
//     window can only open *between* two units of a thread, never inside one:
//     the language's own atomicity is what closes it, structurally, instead of
//     the old "were these two accesses in the same txn?" special case;
//   - "unserializable" is read off **what actually happened**, not looked up in
//     a table of the eight read/write triples. The interpreter already knows
//     exactly what conflicted — a read's version *is* its reads-from edge — so
//     the three cases below are the three ways the intruder is *realized* in the
//     window, each one a relation the store itself computes.
//
// The three:
//
//   - **read-of-intruder** — the victim's second unit reads the value the
//     intruder wrote (a realized reads-from edge from the intruder). Serialize
//     the intruder out of the window and that read observes something else.
//   - **stale-read** — the victim's first unit *read* the address, the intruder
//     then wrote it, and the victim's second unit writes it back. The intruder
//     invalidated the read the write rests on — this is `validate()`'s own test,
//     and outside a transaction there is nothing to abort, so the stale value
//     just lands. The lost update.
//   - **intermediate-read** — the intruder read a value the victim's first unit
//     wrote (again a realized reads-from edge) and the victim's second unit then
//     replaced it. The intruder observed a state that was meant to be private to
//     the pair.
//
// **The one thing that isn't derivation** is knowing which accesses really
// happened. A transaction that aborted (or parked on `retry`, or was still open
// when the run ended) had its accesses rolled back — they never took effect — so
// counting them invents conflicts out of the STM doing its job. `bank.stm` has 30
// of its 74 accesses inside attempts that aborted; keep them and it reports seven
// races, all spurious. That filter lives in `conflict.ts` (a unit exists only for
// a committed transaction), and it is the same one the explorer uses.

import type { Causality } from "../src/backend/interp/causality.js";
import type { RunResult } from "../src/backend/interp/interpreter.js";
import { readFrom, txnUnits } from "./conflict.js";
import type { Access, AccessKind, Unit } from "./conflict.js";

export type { Access, AccessKind } from "./conflict.js";

/** The three-access shape of an interleaving, `first-middle-last`. Descriptive
 *  now, not decisive: it says what the reported accesses were, and the four
 *  values it can take are exactly the four unserializable triples — but they
 *  fall out of `Violation` rather than being looked up in a table. */
export type Pattern = `${AccessKind}-${AccessKind}-${AccessKind}`;

/** Why the window is unserializable — the realized relation that makes the
 *  intruder's position matter. See the module comment. */
export type Violation = "read-of-intruder" | "stale-read" | "intermediate-read";

/** What each violation means, in the victim's voice — for the panel. */
export const VIOLATION_MEANING: Record<Violation, string> = {
  "read-of-intruder": "reads back a value another thread wrote in between",
  "stale-read": "writes back a value computed from a read that is now stale — a lost update",
  "intermediate-read": "lets another thread observe a value it was in the middle of replacing",
};

/** A pair of statements the detector thinks might not be atomic, and the
 *  intruding access that came between them. Proposed, not confirmed. */
export interface RaceCandidate {
  addr: string;
  /** The relation that makes the intrusion matter. */
  violation: Violation;
  /** The shape of the three reported accesses, for display and dedup. */
  pattern: Pattern;
  /** The thread whose two units bracket the intrusion. */
  victim: number;
  /** The thread that ran in between. */
  intruder: number;
  /** The victim's earlier access. */
  first: Access;
  /** The intruder's access, between the victim's two units. */
  middle: Access;
  /** The victim's later access. */
  last: Access;
  /** The units the three accesses belong to (`txn:3`, `auto:17`) — what the
   *  detector actually reasoned about. Two accesses of one unit can never be a
   *  window, so a candidate always names three distinct units. */
  units: { first: string; middle: string; last: string };
}

/** The candidate's meaning, in the victim's voice. */
export function meaningOf(c: RaceCandidate): string {
  return VIOLATION_MEANING[c.violation];
}

/** The unit's accesses to one address, in trace order. */
function accessesOn(u: Unit, addr: string, kind?: AccessKind): Access[] {
  return u.accesses.filter((a) => a.addr === addr && (kind === undefined || a.kind === kind));
}

/** The one closest to the window: the victim's *last* touch before it, or the
 *  intruder's last write into it. */
function lastOn(u: Unit, addr: string, kind?: AccessKind): Access | null {
  const list = accessesOn(u, addr, kind);
  return list.length > 0 ? list[list.length - 1]! : null;
}

function firstOn(u: Unit, addr: string, kind?: AccessKind): Access | null {
  return accessesOn(u, addr, kind)[0] ?? null;
}

/**
 * The unserializable windows in one run.
 *
 * Pure and single-run: it reports what these events show, undeduplicated, with
 * no judgement about whether the interleaving was *reachable any other way* —
 * that is the oracle's job (step 2). Expect false positives here by design;
 * `merge`/`futures`/`channels` produce several from library code that wants
 * exactly this shape. What it must get right is the unit rule: `counter`,
 * `bank`, and `map_reduce` have no real races and must come back empty, and
 * `lost_update`'s `withdraw` pair must be found.
 *
 * Needs a causality-bearing run (`RunOptions.causality`): the reads-from edges
 * two of the three cases turn on are recorded by the interpreter, and guessing
 * them back from event shapes is exactly what this stopped doing.
 */
export function raceCandidates(result: RunResult): RaceCandidate[] {
  const causality = result.causality;
  if (!causality) {
    throw new Error("raceCandidates needs a run with `causality: true` — the reads-from edges are its input");
  }
  const { units, unitOfRow } = txnUnits(result.trace);

  // The addresses anyone touched, each with the units that touched it, in the
  // order the run serialized them.
  const byAddr = new Map<string, Unit[]>();
  for (const u of units) {
    for (const addr of [...u.reads, ...u.writes]) {
      const list = byAddr.get(addr);
      if (list) {
        if (list[list.length - 1] !== u) list.push(u);
      } else byAddr.set(addr, [u]);
    }
  }

  const out: RaceCandidate[] = [];
  for (const [addr, list] of byAddr) {
    for (let i = 0; i < list.length; i++) {
      const victimFirst = list[i]!;
      // The victim's *next* unit on this address, and everyone else's between.
      const between: Unit[] = [];
      let victimLast: Unit | null = null;
      for (let j = i + 1; j < list.length; j++) {
        if (list[j]!.thread === victimFirst.thread) {
          victimLast = list[j]!;
          break;
        }
        between.push(list[j]!);
      }
      if (!victimLast || between.length === 0) continue;
      for (const intruder of between) {
        const found = classify(victimFirst, intruder, victimLast, addr, causality, unitOfRow);
        if (found) out.push(found);
      }
    }
  }
  return out;
}

/**
 * Decide one window, and pick the three accesses that show it.
 *
 * Each case names its own accesses, because which one is worth showing depends
 * on which relation fired: a stale read wants the read that went stale, an
 * intermediate read wants the write that was observed.
 */
function classify(
  first: Unit,
  middle: Unit,
  last: Unit,
  addr: string,
  causality: Causality,
  unitOfRow: ReadonlyMap<number, Unit>
): RaceCandidate | null {
  // read-of-intruder: the victim's second unit read what the intruder wrote.
  const readsIntruder = readFrom(last, middle, addr, causality, unitOfRow);
  if (readsIntruder !== null) {
    const firstAccess = lastOn(first, addr);
    const middleAccess = lastOn(middle, addr, "write");
    const lastAccess = last.accesses.find((a) => a.row === readsIntruder);
    if (firstAccess && middleAccess && lastAccess) {
      return build("read-of-intruder", addr, firstAccess, middleAccess, lastAccess, first, middle, last);
    }
  }

  // stale-read: the victim read the address, the intruder wrote it, and the
  // victim's second unit writes it back. This is `validate()`'s own staleness
  // test — and it needs no version comparison here, because units are ordered by
  // their commit, so an intruder *inside* the window published after the
  // victim's read by construction. What is missing is the abort: there is no
  // transaction around the pair to fail validation, so the stale value lands.
  const staleRead = lastOn(first, addr, "read");
  const intruderWrite = lastOn(middle, addr, "write");
  const restingWrite = firstOn(last, addr, "write");
  if (staleRead && intruderWrite && restingWrite) {
    return build("stale-read", addr, staleRead, intruderWrite, restingWrite, first, middle, last);
  }

  // intermediate-read: the intruder read a value the victim's first unit wrote,
  // and the victim's second unit then replaced it.
  const observed = readFrom(middle, first, addr, causality, unitOfRow);
  if (observed !== null && restingWrite) {
    const firstAccess = lastOn(first, addr, "write");
    const middleAccess = middle.accesses.find((a) => a.row === observed);
    if (firstAccess && middleAccess) {
      return build("intermediate-read", addr, firstAccess, middleAccess, restingWrite, first, middle, last);
    }
  }

  return null;
}

function build(
  violation: Violation,
  addr: string,
  first: Access,
  middle: Access,
  last: Access,
  firstUnit: Unit,
  middleUnit: Unit,
  lastUnit: Unit
): RaceCandidate {
  return {
    addr,
    violation,
    pattern: `${first.kind}-${middle.kind}-${last.kind}` as Pattern,
    victim: first.thread,
    intruder: middle.thread,
    first,
    middle,
    last,
    units: { first: firstUnit.id, middle: middleUnit.id, last: lastUnit.id },
  };
}
